<template>
    <transition name="appointment">
        <div v-if="open" class="appointment-modal-container">
            <div v-if="checklistLoading" class="apm__body" role="status">{{ $t('Carregando...') }}</div>
            <div v-else-if="checklistError" class="apm__body" role="alert">
                <p>{{ $t('ProductionProgress.ChecklistLookupError') }}</p>
                <Button @click="$emit('retryChecklist')">{{ $t('ProductionProgress.ChecklistRetry') }}</Button>
                <Button v-if="beforeChecklist" @click="$emit('cancelBeforeChecklist')">{{ $t('Cancel') }}</Button>
            </div>
            <template v-else-if="!showChecklistForm">
                <SelectModal v-if="selectModal.open" translation-base="ProductionProgress.SelectModal."
                    :title="selectModal.title" :selected-id="selectModal.selectedId" :list="selectModal.data"
                    @select-item="handleSelectModalClick" @close="selectModal.open = false" />

                <InputModal v-if="inputModal" :data="selectedFieldInputModal"
                    @setQuantityValue="(context) => setValueInputModal(context)"
                    @close="(title) => handleInputModal(title, 'close')" />

                <AppointmentHeader :component-id="'appointment-modal'" :back-label="headerBackLabel"
                    :back-event="handleCloseModal" :orders="orderListDisplay" :status="headerStatus"
                    :color="headerColor" :is-fixed="false" :orderObject="orderObject" :show-total-time="false"
                    @removeAppointment="removeAppointment" />

                <div class="fields-container">
                    <div class="fields-wrapper">
                        <SetupAlert v-show="showSetupAlert()">
                            {{ $t("ProductionProgress.AppointmentModal.SetupAlert") }}
                        </SetupAlert>
                        <div class="fields-list">
                            <FieldCard v-if="!isPointResourcePage && data.resource" :data="data.resource" />
                            <FieldCard v-if="isPointResourcePage && data.material" :data="data.material" />
                            <FieldCard v-if="isPointResourcePage && data.order" :data="data.order" />
                            <DateTimePicker v-if="!isPointResourcePage && data.date" v-model="datepicker"
                                :disabled="!!data.date.disabled" type="datetime" position="left" show-seconds
                                :starts-with-time="true" :show-dates="showDates()" :block-dates="blockDates"
                                :week-days="setWeekDays()" :month-names="setMonths()"
                                :txt-cancel-button="$t('Datepicker.Cancel')" :txt-save-button="$t('Datepicker.Save')"
                                :txt-time="$t('Datepicker.Time')" :txt-date="$t('Datepicker.Date')"
                                @input="handleDatepickerChange">
                                <template v-slot:input>
                                    <FieldCard :data="data.date" />
                                </template>
                            </DateTimePicker>

                            <FieldCard v-for="(field, index) in fields" :key="`field-${field}`" :class="[
                                fields.length % 2 !== 0 && index === fields.length - 1
                                    ? 'grid-full'
                                    : ''
                            ]" v-if="data[field]" :data="data[field]" />

                            <FieldCard
                                v-if="showMultipleRefusesCard"
                                :data="multipleRefusesField"
                            />

                            <FieldCard
                                v-if="data.totalTime"
                                :data="data.totalTime"
                            />

                            <CustomFields v-for="(field, index) in filteredCustomFields" :key="`field-${field.id}`"
                                :class="[
                                    customFields.length % 2 !== 0 && index === customFields.length - 1
                                        ? 'grid-full'
                                        : ''
                                ]" :field="field" @loadingPhoto="(v) => (loadingPhoto = v)" />
                        </div>

                        <div class="apm__button-c">
                            <Button v-if="!isPointResourcePage" :disabled="loadingPhoto" @click="confirmAppointment">
                                {{ $t("ProductionProgress.AppointmentModal.ConfirmAppointment") }}
                            </Button>
                            <Button v-else @click="confirmAppointment">
                                {{ $t("MakeAppointment") }}
                            </Button>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <p v-if="checklistContext" class="p-2 mb-0">{{ checklistContext }}</p>
                <ChecklistForm :key="`${checklistData.id}:${checklistOperationId}:${checklistOperationProgressId}`" :checklist="checklistData"
                    :show-cancel-button="!!checklistTriggerType && checklistTriggerType.startsWith('Event_before_')" @cancel="$emit('cancelBeforeChecklist')"
                    :checklist-values-id="currentChecklistValuesId" :submission-error="checklistSubmissionError"
                    @submit="handleChecklistSubmit" @finish="handleChecklistFinish" />
            </template>
        </div>
    </transition>
</template>

<script>
import { closeTooltips } from "@core/utils/utils";
import DateTimePicker from "@/components/components-code/date-time-picker/DatePickerFull.vue";
import AppointmentHeader from "@/views/pages/operator/components/AppointmentHeader";
import FieldCard from "@/views/pages/operator/components/AppointmentModal/components/FieldCard/FieldCard.vue";
import SetupAlert from "@/views/pages/operator/components/AppointmentModal/components/SetupAlert/SetupAlert.vue";
import Button from "@/views/pages/operator/components/AppointmentModal/components/Button/Button.vue";
import InputModal from "@/views/pages/operator/components/InputModal/InputModal.vue";
import SelectModal from "@/views/pages/operator/components/SelectModal/SelectModal.vue";
import ChecklistForm from "@/views/pages/operator/components/ChecklistForm/ChecklistForm.vue";
import moment from "moment";
import CustomFields from "../customFields";
import { http3 } from "@/libs/axios";
import { isFinishedOperationStatus } from "@/views/pages/operator/utils";

export default {
    components: {
        AppointmentHeader,
        FieldCard,
        SetupAlert,
        Button,
        InputModal,
        SelectModal,
        DateTimePicker,
        CustomFields,
        ChecklistForm
    },
    props: {
        appointmentType: {
            default: false
        },
        allowChangeDate: {
            default: false
        },
        defaultProducedQuantity: {
            default: 0
        },
        orderObject: {
            default: null
        },
        showToast: {
            default: () => { }
        },
        formatDatetime: {
            default: () => { }
        },
        defaultDate: { type: Object, default: {} },
        allowChangeResource: {
            type: Boolean
        },
        allowChangeMaterial: {
            type: Boolean
        },
        allowChangeOrder: {
            type: Boolean
        },
        defaultResource: {
            type: Object
        },
        defaultMaterial: {
            type: Object
        },
        defaultOrder: {
            type: Object
        },
        title: {
            type: "Setup" | "Start" | "Partial" | "Continue" | "Stop" | "Finish",
            default: "Partial"
        },
        open: {
            type: Boolean,
            default: false
        },
        resourcesList: {
            type: Array,
            default: []
        },
        materialList: {
            type: Array,
            default: () => []
        },
        orderList: {
            type: Array,
            default: () => []
        },
        rejectionList: {
            type: Array,
            default: []
        },
        statusList: {
            type: Array,
            default: []
        },
        isMultipleOrders: {
            type: Boolean,
            default: false
        },
        orderListDisplay: {
            type: Array,
            default: () => []
        },
        customFields: {
            type: Array,
            default: () => []
        },
        exhibitionLocationId: {
            type: Number,
            default: null
        },
        isPointResourcePage: {
            type: Boolean,
            default: false
        },
        hasRNC: {
            type: Boolean,
            default: false
        },
        checklistOperationProgressId: { type: Number, default: null },
        checklistOperationId: { type: Number, default: null },
        checklistResourceId: { type: Number, default: null },
        checklistLoading: { type: Boolean, default: false },
        checklistContext: { type: String, default: '' },
        checklistError: { type: Boolean, default: false },
        beforeChecklist: { type: Boolean, default: false },
        checklistData: {
            type: Object,
            default: null
        },
        checklistTriggerType: {
            type: String,
            default: null
        },
        checklistTriggerTarget: {
            default: null
        },
        shouldShowChecklist: {
            type: Boolean,
            default: false
        },
        multipleRefuses: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            datepicker: new Date(),
            lang: localStorage.getItem("language"),
            triedToSave: false,
            data: {},
            inputModal: false,
            loadingPhoto: false,
            selectModal: {
                selectedId: null,
                open: false,
                title: "",
                data: []
            },
            showChecklistForm: false,
            localChecklistData: null,
            localChecklistTriggerType: null,
            localChecklistTriggerTarget: null,
            currentChecklistValuesId: null,
            checklistSubmissionError: false,
            inputListModal: false,
            totalOpInterval: null,
            selectedFieldInputModal: {
                title: "",
                quantityProp: 0
            }
        };
    },
    computed: {
        headerBackLabel() {
            return this.$t(`ProductionProgress.AppointmentModal.${this.title}`);
        },
        headerStatus() {
            return this.orderObject?.lastInfo?.lastStatus;
        },
        headerColor() {
            return this.orderObject?.lastInfo?.lastColorStatus;
        },
        shouldFillDefaultValues() {
            const screensWithValues = ["Partial"];
            return screensWithValues.includes(this.title);
        },
        isSomePartialFieldFilled() {
            return (
                ["produced", "rework"].some(
                    (field) => this.data[field]?.value
                ) || (this.multipleRefuses && this.multipleRefuses.length > 0)
            );
        },
        fields() {
            return this.setFields();
        },
        form() {
            const fields = Object.keys(this.data);
            const payload = {};

            fields.forEach((field) => {
                payload[field] = this.data[field].value;
            });

            return payload;
        },
        filteredCustomFields() {
            return this.customFields.filter((field) =>
                field.exhibitionLocationIds.includes(this.exhibitionLocationId)
            );
        },
        currentOperationProgressId() {
            return this.orderObject?.lastInfo?.lastOperationProgressId;
        },
        showMultipleRefusesCard() {
            return (
                this.title === "Partial" &&
                !this.isMultipleOrders
            );
        },
        totalMultipleRefusesQuantity() {
            if (!this.multipleRefuses || this.multipleRefuses.length === 0) return 0;
            return this.multipleRefuses.reduce((sum, entry) => {
                const qty = this.formatedNumber(entry.quantity);
                return sum + (isNaN(qty) ? 0 : qty);
            }, 0);
        },
        multipleRefusesField() {
            const count = this.multipleRefuses?.length || 0;
            const total = this.totalMultipleRefusesQuantity;
            const displayValue = count === 0
                ? 0
                : count === 1
                    ? total
                    : `${total} (${count})`;

            return {
                title: "Rejection",
                value: displayValue,
                disabled: false,
                alert: false,
                action: () => this.$emit("openMultipleRefuses")
            };
        },
        isMultipleOrdersByResource() {
            return (
                this.$router.currentRoute.name === "operator-production-progress-multiple" &&
                (this.$route.query.perOrderQuantity === "true" ||
                    this.$route.query.fromMultipleOrdersByResource === "true")
            );
        }
    },
    watch: {
        async open(isOpen) {
            if (isOpen) {
                this.closeAllModals();
                this.isPointResourcePage && this.$emit("open");
                this.stopTotalOpTimer();
                this.setData();
                this.startTotalOpTimer();
                this.applyChecklistFromProps();
            } else {
                this.stopTotalOpTimer();
                this.showChecklistForm = false;
            }
        },
        multipleRefuses: {
            deep: true,
            handler() {
                if (this.open && this.title === "Partial") {
                    this.validateModalInputFields();
                }
            }
        },
        checklistData(newVal, oldVal) {
            if (newVal && this.shouldShowChecklist && this.open && !this.hasRNC && !this.showChecklistForm) {
                if (newVal !== oldVal || (newVal && !oldVal)) {
                    this.localChecklistData = newVal;
                    this.localChecklistTriggerType = this.checklistTriggerType;
                    this.localChecklistTriggerTarget = this.checklistTriggerTarget;
                    this.showChecklistForm = true;
                }
            }
        },
        shouldShowChecklist(newVal, oldVal) {
            if (newVal && this.checklistData && this.open && !this.hasRNC && !this.showChecklistForm) {
                if (newVal !== oldVal && newVal === true) {
                    this.localChecklistData = this.checklistData;
                    this.localChecklistTriggerType = this.checklistTriggerType;
                    this.localChecklistTriggerTarget = this.checklistTriggerTarget;
                    this.showChecklistForm = true;
                }
            }
        },
        defaultResource: {
            deep: true,
            handler() {
                if (this.open) {
                    this.stopTotalOpTimer();
                    this.setData();
                this.startTotalOpTimer();
                }
            }
        },
        resourcesList: {
            deep: true,
            handler() {
                if (this.open) {
                    this.stopTotalOpTimer();
                    this.setData();
                this.startTotalOpTimer();
                }
            }
        }
    },
    updated() {
        const rootElement = document.querySelector(".appointment-modal-container");
        rootElement && closeTooltips(rootElement);
    },
    beforeDestroy() {
        this.stopTotalOpTimer();
    },
    mounted() {
        this.$watch("$i18n.locale", function () {
            this.handleLanguageChange();
        });
    },
    methods: {
        headerBackEvent() { },
        formatDurationHms(seconds) {
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);
            return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
        },
        calculateTotalOpDuration() {
            const order = this.orderObject;
            if (!order) return "00:00:00";
            const status = order.lastInfo?.lastStatus;
            const normStatus = (status || "").trim().toLowerCase();
            if (["", "não iniciado", "not started"].includes(normStatus)) {
                return "00:00:00";
            }
            const isFinished = isFinishedOperationStatus(status) ||
                ["operação finalizada", "operacao finalizada", "completed", "finalizado", "finalizada", "concluído", "concluido"].includes(normStatus);
            const startTimeStr = order.lastInfo?.firstEventTime || order.startTime || order.lastInfo?.lastStarted || (!isFinished ? order.lastInfo?.lastEventTime : null);
            if (!startTimeStr) return "00:00:00";
            const startedAt = Date.parse(startTimeStr);
            if (!Number.isFinite(startedAt)) return "00:00:00";
            if (isFinished) {
                const finishTimeStr = order.finishedAt || order.lastInfo?.lastCompleted || order.lastInfo?.lastCompletedByUser || order.endTime || order.lastInfo?.lastEventTime;
                const finishedAt = Date.parse(finishTimeStr);
                const elapsed = Number.isFinite(finishedAt) && finishedAt >= startedAt ? Math.floor((finishedAt - startedAt) / 1000) : 0;
                return this.formatDurationHms(elapsed);
            }
            const elapsed = Math.floor(Math.max(0, Date.now() - startedAt) / 1000);
            return this.formatDurationHms(elapsed);
        },
        updateTotalOpDuration() {
            if (this.data && this.data.totalTime) {
                this.data.totalTime.value = this.calculateTotalOpDuration();
            }
        },
        startTotalOpTimer() {
            this.stopTotalOpTimer();
            this.updateTotalOpDuration();
            const status = this.orderObject?.lastInfo?.lastStatus;
            const isFinished = isFinishedOperationStatus(status) ||
                ["operação finalizada", "operacao finalizada", "completed", "finalizado", "finalizada", "concluído", "concluido"].includes(((status || "").trim().toLowerCase()));
            if (isFinished) return;
            this.totalOpInterval = setInterval(() => {
                this.updateTotalOpDuration();
            }, 1000);
        },
        stopTotalOpTimer() {
            if (this.totalOpInterval) {
                clearInterval(this.totalOpInterval);
                this.totalOpInterval = null;
            }
        },
        removeAppointment(appointmentId) {
            this.$emit("removeAppointment", appointmentId);
        },
        showDates() {
            const currentYear = new Date().getFullYear();
            const firstDayPreviousYear = new Date(currentYear - 1, 0, 1);
            const today = new Date(new Date().setDate(new Date().getDate() + 1));

            return [firstDayPreviousYear, today];
        },
        blockDates(date) {
            const currentDate = moment(moment().format("YYYY-MM-DD"));
            const inspected = moment(moment(new Date(date)).format("YYYY-MM-DD"));

            return inspected > currentDate;
        },
        setWeekDays() {
            const weekdays = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday"
            ];

            return weekdays.map((day) => ({
                name: this.$t(`Datepicker.Weekdays.Full.${day}`),
                short: this.$t(`Datepicker.Weekdays.Short.${day}`)
            }));
        },
        setMonths() {
            const months = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December"
            ];

            return months.map((month) => ({
                name: this.$t(`Datepicker.Months.Full.${month}`),
                short: this.$t(`Datepicker.Months.Short.${month}`)
            }));
        },
        handleDatepickerChange(date) {
            this.data.date.value = this.formatDatetime(date);
        },
        handleLanguageChange() {
            this.lang = this.$i18n.locale;
            this.data.date.value = this.formatDatetime(this.datepicker);
        },
        getLastAppointment() {
            const order = this?.orderObject;
            return moment(order?.lastInfo?.lastEventTime).toDate();
        },
        validateRequiredFields(fields) {
            return !fields.some(
                (field) =>
                    field.required && (field.value == null || String(field.value).trim() === "")
            );
        },
        validateForm() {
            const screen = String(this.title).toLowerCase();
            // Definindo quais campos devem ser validados
            const initialFields = ["date"];
            const fields = {
                stop: ["status"],
                partial: []
            };
            const fieldsWithQuantity = ["produced", "rework"];

            const fieldsToValidate = screen in fields ? fields[screen] : initialFields;

            // Se qualquer um dos campos (produzido, retrabalho, refugo) estiver preenchido, permite envio
            if (screen === "partial" && !this.isMultipleOrders) {
                const filledAnyField =
                    fieldsWithQuantity.some((field) => !!this.data[field]?.value) ||
                    (this.multipleRefuses && this.multipleRefuses.length > 0);

                if (!filledAnyField) {
                    this.showErrorToast("quantityFields");
                    return false;
                }
            }

            const emptyFields = fieldsToValidate.filter((field) => !!this.data?.[field]?.alert);
            const emptyRequiredFields = this.validateRequiredFields(this.filteredCustomFields);

            if (!this.isPointResourcePage && this.data?.resource) {
                if (!this.data.resource.id && !this.data.resource.value) {
                    this.showErrorToast("requiredFields");
                    return false;
                }
            }

            if (emptyFields.length || !emptyRequiredFields) {
                this.showErrorToast("requiredFields");
                return false;
            }

            // Validação de datas
            const lastAppointment = moment(this.getLastAppointment());
            const currentTime = moment(new Date());
            const selectedDate = moment(this.datepicker);

            const isHigherThanCurrentTime = selectedDate.isAfter(currentTime);
            if (isHigherThanCurrentTime) {
                this.showErrorToast("higherThanCurrentTime");
                return false;
            }

            // Setup permite data anterior ao último apontamento
            if (this.title === "Setup") return true;

            const isLowerThanLastAppointment = moment(selectedDate).isBefore(lastAppointment);

            if (!!lastAppointment && isLowerThanLastAppointment) {
                this.showErrorToast("lowerThanLastAppointment");
                return false;
            }

            return true;
        },
        showErrorToast(type) {
            const types = {
                requiredFields: {
                    title: this.$t("ProductionProgress.Toasts.RequiredFields.Title"),
                    icon: "AlertOutline",
                    text: this.$t("ProductionProgress.Toasts.RequiredFields.Content"),
                    variant: "alert"
                },
                quantityFields: {
                    title: this.$t("ProductionProgress.Toasts.QuantityFieldsRequired.Title"),
                    icon: "AlertOutline",
                    text: this.$t("ProductionProgress.Toasts.QuantityFieldsRequired.Content"),
                    variant: "alert"
                },
                higherThanCurrentTime: {
                    title: this.$t("ProductionProgress.Toasts.HigherThanCurrentTime.Title"),
                    text: this.$t("ProductionProgress.Toasts.HigherThanCurrentTime.Content"),
                    variant: "danger"
                },
                lowerThanLastAppointment: {
                    title: this.$t("ProductionProgress.Toasts.LowerThanLastAppointment.Title"),
                    text: this.$t("ProductionProgress.Toasts.LowerThanLastAppointment.Content"),
                    variant: "danger"
                }
            };

            const variant = types[type];
            this.showToast(variant.title, variant.icon, variant.text, variant.variant);
        },
        confirmAppointment() {
            const isFormValid = this.validateForm();

            if (!isFormValid) return;

            this.$emit("confirmAppointment", {
                ...this.data,
                datepicker: this.datepicker
            });
        },
        async handleChecklistSubmit(checklistFields) {
            this.checklistSubmissionError = false;
            try {
                const beforeEvent = this.checklistTriggerType?.startsWith('Event_before_');
                const operationProgressId = beforeEvent ? null : this.checklistOperationProgressId ?? this.orderObject?.lastInfo?.lastOperationProgressId;
                if (!beforeEvent && !operationProgressId) throw new Error('Missing operation progress');

                const body = {
                    checklistId: this.checklistData?.id,
                    operationProgressId: operationProgressId,
                    operationId: beforeEvent ? this.checklistOperationId : undefined,
                    resourceId: beforeEvent ? this.checklistResourceId : undefined,
                    isPriority: false,
                    triggerType: this.checklistTriggerType,
                    triggerTarget: this.checklistTriggerTarget,
                    fields: checklistFields.map(field => ({
                        value: String(field.value ?? ""),
                        fieldId: field.id
                    }))
                };

                const response = await http3.post(`api/myquality/checklistValues`, body);
                const checklistValuesId = response?.data?.data?.checklistValuesId;

                if (!checklistValuesId) throw new Error('Missing checklist response ID');
                if (checklistValuesId) {
                    this.currentChecklistValuesId = checklistValuesId;
                }

                this.showToast(
                    this.$t('Sucesso'),
                    'CheckIcon',
                    this.$t('Checklist enviado com sucesso'),
                    'success'
                );

                if (beforeEvent || !this.checklistData?.hasValidation) {
                    this.stopTotalOpTimer();
                    this.showChecklistForm = false;
                    this.localChecklistTriggerType = null;
                    this.localChecklistTriggerTarget = null;
                    this.currentChecklistValuesId = null;
                    this.checklistSubmissionError = false;
                    this.$emit("checklistCompleted", checklistFields);
                }
            } catch (error) {
                console.error("Error submitting checklist:", error);
                this.checklistSubmissionError = true;
                this.showToast(
                    this.$t('Erro'),
                    'AlertTriangleIcon',
                    this.$t('Erro ao enviar checklist'),
                    'danger'
                );
            }
        },
        handleChecklistFinish() {
            this.showChecklistForm = false;
            this.localChecklistTriggerType = null;
            this.localChecklistTriggerTarget = null;
            this.currentChecklistValuesId = null;
            this.checklistSubmissionError = false;
            this.$emit("checklistCompleted");
        },
        showDatepicker() {
            this.datepicker.show = !this.datepicker.show;
        },
        handleCloseModal() {
            this.stopTotalOpTimer();
            this.setData();
            this.$emit("close");
        },
        setFields() {
            const { title, isMultipleOrders } = this;
            const partialFields = ["produced", "rework"];

            const stopFields = ["status"];

            if (title === "Partial" && !isMultipleOrders) {
                return partialFields;
            }

            if (title === "Stop") {
                return stopFields;
            }

            return [];
        },
        returnDefaultDate() {
            // Para aguardar a chamada da API sem quebrar o mount
            const didFetchValues = !!this?.defaultDate?.start || !!this?.defaultDate?.setup;

            if (!didFetchValues || !this.allowChangeDate || this.isMultipleOrders) return new Date();

            const card = this.title.toLowerCase();

            if (card === "start") return this.defaultDate.start;
            if (card === "setup") return this.defaultDate.setup;
            return new Date();
        },
        setData() {
            const setValueProduced = () => {
                return this.shouldFillDefaultValues ? this?.defaultProducedQuantity : 0;
            };

            const isBlockedResource = this.isMultipleOrdersByResource;
            const targetResId = this?.defaultResource?.value || (isBlockedResource ? this.$route.params.resource_id : null);
            const foundResource = this?.resourcesList?.find(
                (item) => item?.value == targetResId
            );
            const resourceText = foundResource?.text || this?.defaultResource?.text || null;
            const resourceId = targetResId ? Number(targetResId) : null;
            const isResourceDisabled = isBlockedResource
                ? true
                : (!!targetResId ? !this?.allowChangeResource : false);

            this.datepicker = this.returnDefaultDate() ?? new Date();
            this.data = {
                resource: {
                    title: "Resource",
                    value: resourceText,
                    disabled: isResourceDisabled,
                    alert: !resourceText && !resourceId,
                    action: this.handleSelectModal,
                    id: resourceId
                },
                material: {
                    title: "Material",
                    value: this?.defaultMaterial?.text ? this?.defaultMaterial?.text : "-",
                    disabled: !this?.allowChangeMaterial && this?.defaultMaterial,
                    alert: (!this?.defaultMaterial?.value && !this?.defaultMaterial?.text) ?? !this?.data?.material?.id,
                    action: this.handleSelectModal,
                    id: Number(this?.defaultMaterial?.value) ?? null
                },
                order: {
                    title: "Order",
                    value: this?.defaultOrder?.text ? this?.defaultOrder?.text : "-",
                    disabled: !this?.allowChangeOrder && this?.defaultOrder,
                    alert: !this?.defaultOrder?.value ?? !this?.data?.material?.id,
                    action: this.handleSelectModal,
                    id: Number(this?.defaultOrder?.value) ?? null
                },
                date: {
                    title: "Date",
                    value: this.formatDatetime(this.datepicker),
                    disabled: !this?.allowChangeDate,
                    alert: false,
                    action: () => {
                        // this.openDatepicker();
                    }
                },
                produced: {
                    title: "Produced",
                    value: setValueProduced(),
                    disabled: this.isMultipleOrders,
                    alert: true,
                    validations: {
                        validateModalInputFields: this.validateModalInputFields,
                        validateProducedRemaining: this.validateProducedRemaining
                    },
                    action: this.handleInputModal
                },
                rework: {
                    title: "Rework",
                    value: 0,
                    disabled: this.isMultipleOrders,
                    alert: true,
                    validations: {
                        validateModalInputFields: this.validateModalInputFields
                    },
                    action: this.handleInputModal
                },
                rejection: {
                    title: "Rejection",
                    value: 0,
                    disabled: true,
                    alert: false
                },
                reason: {
                    title: "Reason",
                    value: "",
                    id: null,
                    disabled: true,
                    alert: false
                },
                status: {
                    title: "Status",
                    value: "",
                    disabled: false,
                    alert: true,
                    action: this.handleSelectModal
                }
            };

            this.data.totalTime = {
                title: "TotalTime",
                value: this.calculateTotalOpDuration(),
                disabled: true,
                alert: false
            };

            if (this.isMultipleOrders) this.setDataInMultipleAppointment();
            if (this.data.produced) this.validateModalInputFields();
        },
        setDataInMultipleAppointment() {
            const {
                formatDatetime,
                allowChangeDate,
                isMultipleOrders,
                data: { date }
            } = this;

            if (!isMultipleOrders) return;

            this.data.date = {
                ...date,
                value: formatDatetime(this.datepicker),
                disabled: !allowChangeDate
            };

            if (this.isMultipleOrdersByResource && this.data.resource) {
                const targetResId = this?.defaultResource?.value || this.$route.params.resource_id;
                const foundResource = this?.resourcesList?.find(
                    (item) => item?.value == targetResId
                );
                const resourceText = foundResource?.text || this?.defaultResource?.text;
                const resourceId = targetResId ? Number(targetResId) : null;

                if (resourceText) {
                    this.data.resource.value = resourceText;
                }
                if (resourceId) {
                    this.data.resource.id = resourceId;
                }
                this.data.resource.disabled = true;
                this.data.resource.alert = !this.data.resource.value && !this.data.resource.id;
            }
        },
        closeAllModals() {
            this.inputModal = false;
            this.selectModal.open = false;
        },
        handleInputModal(field, action = "open") {
            const props = action === "open" && {
                title: field,
                quantityProp: this.data[field.toLowerCase()].value
            };

            this.selectedFieldInputModal = action === "open" ? props : {};
            this.inputModal = action === "open";
        },
        handleSelectModal(field, action = "open") {
            this.selectModal.open = !!(action === "open");
            this.selectModal.title = field;
            this.loadModalOptions(field);
        },
        loadModalOptions(field) {
            let statusList = this.statusList;

            const formatListItems = (list) =>
                list.map((item) => ({ ...item, name: item?.text, id: item.value }));

            if (this.title === "Stop") {
                statusList = this.statusList.filter(
                    (e) =>
                        (e.status_classification_id === 1 && (e.name !== "Setup" || e.value === 2)) ||
                        e.status_classification_id === 2 ||
                        e.status_classification_id === 3
                );
            }

            if (field === "Status") {
                statusList = [...statusList].sort((a, b) =>
                    String(a.text ?? a.name ?? "").localeCompare(
                        String(b.text ?? b.name ?? ""),
                        "pt",
                        { sensitivity: "base", numeric: true }
                    )
                );
                this.selectModal.data = formatListItems(statusList);
            } else if (field === "Reason") {
                this.selectModal.data = formatListItems(this.rejectionList);
            } else if (field === "Resource") {
                this.selectModal.data = formatListItems(this.resourcesList);
            } else if (field === "Material") {
                this.selectModal.data = formatListItems(this.materialList);
            } else if (field === "Order") {
                this.selectModal.data = formatListItems(this.orderList);
            }

            this.selectModal.selectedId = this?.data[String(field).toLowerCase()]?.id ?? null;
        },
        handleSelectModalClick(value) {
            const field = String(value.title).toLowerCase();
            this.selectModal.selectedId = value?.item.id;
            this.data[field].value = value.item.name;
            this.data[field].id = value.item.id;
            this.data[field].alert = false;
            if (field === "order") this.data[field].operationId = value.item.operationId;
            this.$emit("changeFields", value);
        },
        validateModalInputFields() {
            const keys = ["produced", "rework"];
            const validatations = keys.map((key) => this.data[key]?.value !== 0);
            const validadation = !validatations.includes(true) && (!this.multipleRefuses || this.multipleRefuses.length === 0);

            for (const key of keys) {
                if (this.data[key]) this.data[key].alert = validadation;
            }
        },
        showSetupAlert() {
            // Somente caso exista algum apontamento anterior
            const order = this?.orderObject;
            const hasPreviousAppointment = !!order?.lastInfo?.lastEventTime;
            const isSetupModal = this.title === "Setup";
            return hasPreviousAppointment && isSetupModal;
        },
        applyChecklistFromProps() {
            if (this.hasRNC) {
                return;
            }

            if (this.open && this.shouldShowChecklist && this.checklistData) {
                this.localChecklistData = this.checklistData;
                this.localChecklistTriggerType = this.checklistTriggerType;
                this.localChecklistTriggerTarget = this.checklistTriggerTarget;
                this.showChecklistForm = true;
            }
        },
        setValueInputModal(context) {
            const { title, quantity } = context;
            const fieldKey = title.toLowerCase();
            if (this.data[fieldKey]) {
                this.data[fieldKey].value = quantity;
            }

            const partialCardFields = ["produced", "rework"];
            const fieldFromPartialCard = partialCardFields.includes(fieldKey);

            if (fieldFromPartialCard) {
                const isAnyFieldFilled = partialCardFields.some(
                    (field) => !!this.data[field]?.value
                ) || (this.multipleRefuses && this.multipleRefuses.length > 0);

                for (const field of partialCardFields) {
                    if (this.data[field]) {
                        this.data[field].alert = !isAnyFieldFilled;
                    }
                }
            }
        },
        formatedNumber(value) {
            if (typeof value !== "string") return Number(value) || 0;
            const cleaned = value.replace(/\./g, "").replace(",", ".");
            return parseFloat(cleaned) || 0;
        }
    }
};
</script>

<style>
.appointment-leave-active {
    transition: opacity 0.25s ease-in-out;
}

.appointment-leave {
    opacity: 100%;
}

.appointment-leave-to {
    opacity: 0%;
}

body {
    touch-action: manipulation;
}
</style>

<style lang="scss" scoped>
.appointment-modal-container {
    overflow: auto;
    height: 100dvh;
    width: 100vw;
    background: #fcfcfc;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-track {
        background: #fff;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #cfc4be;
        border-radius: 9px;
    }

    .fields-container {
        padding: 24px 29px calc(24px + 63px + 24px);
        display: flex;
        flex-grow: 1;

        .fields-wrapper {
            width: 100%;
            display: flex;
            flex-direction: column;
            position: relative;

            .fields-list {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                grid-auto-rows: 124px;
                grid-auto-flow: dense;
                gap: 16px 30px;

                .grid-full {
                    grid-column: span 2;
                }
            }

            .apm__button-c {
                width: 100%;
                padding: 0 24px;
                position: fixed;
                bottom: 24px;
                left: 0;

                button {
                    margin: 0 auto;
                }
            }
        }
    }
}

@media (max-width: 768px) and (min-width: 425px) {
    .appointment-modal-container {
        .fields-container {
            .fields-wrapper {
                .fields-list {
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-auto-rows: 138px !important;

                    .grid-full {
                        grid-column: inherit !important;
                    }
                }
            }
        }
    }
}

@media (max-width: 425px) {
    .appointment-modal-container {
        .fields-container {
            padding: 24px 16px calc(24px + 36px + 16px) !important;

            .fields-wrapper {
                .fields-list {
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-auto-rows: 92px !important;

                    .grid-full {
                        grid-column: inherit !important;
                    }
                }

                .apm__button-c {
                    padding: 0 16px !important;
                    bottom: 16px !important;
                }
            }
        }
    }
}
</style>
